import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { beamSolid, frustumSolid, planPoint } from '../solids';
import { readNumber } from './params';
import { meshFrameOf, planFrameOf } from './planFrame';

export const GALLOWS = {
  postHeight: 4,
  moundHeight: 0.5,
  moundRadius: 3,
  moundTopShare: 0.7,
  moundSegments: 12,
  postSpacing: 2.4,
  postHalf: 0.14,
  beamHalf: 0.12,
  beamOverhang: 0.35,
} as const;

export interface GallowsColors {
  readonly timber: LinearRGB;
  readonly earth: LinearRGB;
}

/** Two posts and a crossbeam on a low earthen mound (van Deventer's pictogram). */
export function buildGallows(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: GallowsColors,
  edgeId: number,
  seed: number,
  warn: (msg: string) => void
): number {
  const g = GALLOWS;
  const frame = meshFrameOf(planFrameOf(landmark.footprint, landmark.heading));
  const postHeight = readNumber(landmark.params, 'height', g.postHeight, warn);
  const base = { edgeId, seed, strokeAngle: 0, extent: [2, postHeight] as const };
  const timber: VertexStyle = { ...base, color: colors.timber, surface: SURFACE.wood };
  const earth: VertexStyle = { ...base, color: colors.earth, surface: SURFACE.ground };
  const before = builder.triangleCount;
  const mound = { center: [0, 0] as const, radius: g.moundRadius, segments: g.moundSegments, phase: 0 };
  frustumSolid(builder, earth, frame, mound, 0, g.moundHeight, g.moundRadius * g.moundTopShare, { top: true, bottom: false });
  const topY = g.moundHeight + postHeight;
  for (const side of [-1, 1]) {
    const u = (side * g.postSpacing) / 2;
    beamSolid(builder, timber, frame, planPoint(u, g.moundHeight - 0.1, 0), planPoint(u, topY, 0), g.postHalf);
  }
  const reach = g.postSpacing / 2 + g.beamOverhang;
  const beamY = topY - g.beamHalf;
  beamSolid(builder, timber, frame, planPoint(-reach, beamY, 0), planPoint(reach, beamY, 0), g.beamHalf);
  return builder.triangleCount - before;
}
